import isString from "lodash/isString";
import map from "lodash/map";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { useMetadata } from "PFApp/components/activity_commons/use_metadata";
import { BookingCategoryBadge } from "PFApp/components/booking_category_badge";
import { Flex, Inline, Stack } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { Tile } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { useMatchableTypes } from "PFCore/helpers/activities";
import { AccessLevel } from "PFCore/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./activity_overview.module.scss";

const metadataToOmit = ["availability", "interest_questions", "interest_statement_enabled"];

export const ActivityOverviewAttributes = () => {
  const { t } = useTranslation("activities", { keyPrefix: "show.details" });
  const activity = useCurrentActivity();
  const matchableTypes = useMatchableTypes({
    templateId: activity.template_id,
    levels: [AccessLevel.ReadWrite, AccessLevel.ReadOnly],
    activityCreator: activity.profile
  });

  const metadata = useMetadata({ activity: activity, metadataToOmit });

  const attributesToShow = useMemo(
    () =>
      metadata.filter(
        ({ name, text }) =>
          text && !matchableTypes.some(({ name: matchableTypeName }) => matchableTypeName === name)
      ),
    [metadata, matchableTypes]
  );

  if (attributesToShow.length === 0) {
    return null;
  }

  return (
    <Tile variant="light" paddingVariant="lg" data-qa-id="ActivityDetailsAttributes">
      <Stack gap="spacingLg">
        <Typography variant="h5">{t("attributes")}</Typography>
        <Flex wrap="wrap" gap="spacingXl">
          {map(attributesToShow, ({ title, text, name }) => (
            <Stack key={title} className={css.attributeCell} qaId={`ActivityDetailsAttribute-${name}`}>
              <Typography variant="bodyRegular">{title}</Typography>
              <Inline gap="spacingSm" alignItems="center">
                {name === "location" && <Icon name="location" size="md" />}
                {name === "booking_category_id" ? (
                  <BookingCategoryBadge categoryId={activity.booking_category_id} />
                ) : isString(text) ? (
                  <Typography
                    variant="bodyBold"
                    className={css.attributeValue}
                    textOverflow="clip"
                    title={text}
                  >
                    {text}
                  </Typography>
                ) : (
                  <Typography variant="bodyBold" className={css.attributeValue}>
                    {text}
                  </Typography>
                )}
              </Inline>
            </Stack>
          ))}
        </Flex>
      </Stack>
    </Tile>
  );
};
